import { MOCK_NOTIFICATIONS } from "@/mocks/notifications";
import { USE_MOCK_API } from "@/services/config";
import { http } from "@/services/http";
import { listPayouts } from "@/services/payouts";
import { getOutstandingReport } from "@/services/reports";
import { getPendingDeviceCount } from "@/services/devices";
import type { NotificationItem } from "@/services/types";

interface DueEntry {
  groupId: string;
  groupName: string;
  cycleNo: number;
  auctionDate: string;
}

let inflightLivePromise: Promise<NotificationItem[]> | null = null;
let cachedLiveItems: NotificationItem[] | null = null;
let lastLiveFetch = 0;
const CACHE_TTL_MS = 30000;

/**
 * Centralized in-app notification service. In real-API mode, items are built
 * fresh on every call from actual current system state — auctions due or
 * overdue, payouts awaiting approval, demands crossed into overdue — rather
 * than stored anywhere. There is no backend notifications table, so "read"
 * state is tracked client-side (localStorage, per browser) against each
 * notification's deterministic id, and re-applied over the freshly computed
 * list every time.
 */

const delay = (ms = 250) => new Promise((resolve) => setTimeout(resolve, ms));

let mockStore: NotificationItem[] = JSON.parse(JSON.stringify(MOCK_NOTIFICATIONS));

/* ================= Real API — derived from live system state ================= */

const READ_IDS_KEY = "kameti.notifications.readIds";

function loadReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_IDS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveReadIds(ids: Set<string>): void {
  try {
    localStorage.setItem(READ_IDS_KEY, JSON.stringify([...ids]));
  } catch {
    // localStorage unavailable (private browsing, quota) — read state just won't persist across reloads
  }
}

function todayDateOnly(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

async function buildLiveNotifications(): Promise<NotificationItem[]> {
  const readIds = loadReadIds();
  const today = todayDateOnly();
  const items: NotificationItem[] = [];

  const [dueData, pendingPayouts, outstanding, pendingDevices] = await Promise.all([
    http<{ dueToday: DueEntry[]; overdue: DueEntry[]; upcoming: DueEntry[] }>("/auctions/due", { method: "GET" }).catch(() => ({ dueToday: [], overdue: [], upcoming: [] })),
    listPayouts({ status: "pending_approval" }).catch(() => []),
    getOutstandingReport().catch(() => null),
    getPendingDeviceCount().catch(() => 0),
  ]);

  if (pendingDevices > 0) {
    const id = `device-approval-summary-${pendingDevices}`;
    items.push({
      id,
      category: "device_approval",
      title: `${pendingDevices} device${pendingDevices === 1 ? "" : "s"} awaiting approval`,
      description: "Staff users have requested login access from new devices. Review and approve or reject.",
      link: "/settings/devices",
      createdAt: new Date().toISOString(),
      read: readIds.has(id),
    });
  }

  for (const a of dueData.overdue ?? []) {
    const id = `auction-overdue-${a.groupId}-${a.cycleNo}`;
    items.push({
      id,
      category: "auction_overdue",
      title: `Auction overdue — ${a.groupName}`,
      description: `Cycle ${a.cycleNo} auction has not been held past its scheduled date.`,
      link: `/auction/${a.groupId}/${a.cycleNo}`,
      createdAt: a.auctionDate,
      read: readIds.has(id),
    });
  }

  for (const a of dueData.dueToday ?? []) {
    const id = `auction-due-${a.groupId}-${a.cycleNo}`;
    items.push({
      id,
      category: "auction_due",
      title: `Auction due today — ${a.groupName}`,
      description: `Cycle ${a.cycleNo} auction is scheduled for today. Open the console to run it.`,
      link: `/auction/${a.groupId}/${a.cycleNo}`,
      createdAt: a.auctionDate,
      read: readIds.has(id),
    });
  }

  // Payouts awaiting approval — one summary item, since a growing count
  // matters more here than each individual voucher.
  if (pendingPayouts.length > 0) {
    const id = `payout-approval-summary-${pendingPayouts.length}`;
    const vouchers = pendingPayouts.map((p) => p.voucherNumber);
    const sample = vouchers.slice(0, 2).join(" and ");
    const description =
      vouchers.length <= 2
        ? `Voucher${vouchers.length === 1 ? "" : "s"} ${sample} ${vouchers.length === 1 ? "is" : "are"} ready for the organizer to approve.`
        : `Vouchers ${sample} and ${vouchers.length - 2} more are ready for the organizer to approve.`;
    const latest = [...pendingPayouts].sort((x, y) => y.createdAt.localeCompare(x.createdAt))[0];
    items.push({
      id,
      category: "payout_approval",
      title: `${pendingPayouts.length} payout${pendingPayouts.length === 1 ? "" : "s"} awaiting approval`,
      description,
      link: "/payout",
      createdAt: latest?.createdAt ?? new Date().toISOString(),
      read: readIds.has(id),
    });
  }

  // Demands crossed into overdue — one summary item across all groups.
  if (outstanding) {
    const overdueCount = outstanding.flat.filter((d) => d.daysOverdue > 0).length;
    if (overdueCount > 0) {
      const id = `demand-overdue-summary-${overdueCount}`;
      const overdueAmount = outstanding.grandTotal - outstanding.bucketTotals.not_due;
      items.push({
        id,
        category: "demand_overdue",
        title: `${overdueCount} demand${overdueCount === 1 ? "" : "s"} crossed into overdue`,
        description: `Totalling ₹${overdueAmount.toLocaleString("en-IN")} past due date. Apply late fees or follow up.`,
        link: "/collection/late-fees",
        createdAt: new Date().toISOString(),
        read: readIds.has(id),
      });
    }
  }

  // Only return unread/active notifications — read ones are cleared
  return items
    .filter((item) => !readIds.has(item.id))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

async function getLiveNotificationsCached(): Promise<NotificationItem[]> {
  const now = Date.now();
  if (cachedLiveItems && now - lastLiveFetch < CACHE_TTL_MS) {
    return cachedLiveItems;
  }
  if (!inflightLivePromise) {
    inflightLivePromise = buildLiveNotifications()
      .then((items) => {
        cachedLiveItems = items;
        lastLiveFetch = Date.now();
        inflightLivePromise = null;
        return items;
      })
      .catch((err) => {
        inflightLivePromise = null;
        throw err;
      });
  }
  return inflightLivePromise;
}

/* ================= Exported service ================= */

export async function listNotifications(): Promise<NotificationItem[]> {
  if (!USE_MOCK_API) {
    return getLiveNotificationsCached();
  }

  const readIds = loadReadIds();
  await delay(80);
  return mockStore
    .filter((n) => !n.read && !readIds.has(n.id))
    .sort((a: NotificationItem, b: NotificationItem) =>
      b.createdAt.localeCompare(a.createdAt)
    );
}

export async function getUnreadCount(): Promise<number> {
  if (!USE_MOCK_API) {
    const items = await getLiveNotificationsCached();
    return items.length;
  }

  const readIds = loadReadIds();
  await delay(40);
  return mockStore.filter((n) => !n.read && !readIds.has(n.id)).length;
}

export async function markNotificationRead(id: string): Promise<void> {
  const ids = loadReadIds();
  ids.add(id);
  saveReadIds(ids);

  if (cachedLiveItems) {
    cachedLiveItems = cachedLiveItems.filter((n) => n.id !== id);
  }
  mockStore = mockStore.filter((n) => n.id !== id);
}

export async function markAllNotificationsRead(): Promise<void> {
  const ids = loadReadIds();
  if (USE_MOCK_API) {
    for (const item of mockStore) ids.add(item.id);
    mockStore = [];
  } else {
    const currentItems = cachedLiveItems || (await buildLiveNotifications());
    for (const item of currentItems) ids.add(item.id);
    cachedLiveItems = [];
  }
  saveReadIds(ids);
}

export function clearNotificationCache(): void {
  cachedLiveItems = null;
  lastLiveFetch = 0;
}

export const notificationsService = {
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  clearNotificationCache,
};

